/**
 * Who CHECKS a feature code — the question behind "does granting this do
 * anything?", answered the same way for every list that offers a code.
 *
 * Three things check codes, and the catalog knows each one as data:
 *
 *   console  a capability (common.FeatureSurfaces): Fusion checks it, and
 *            the lock renders where the capability lives.
 *   content  a base map or map layer (backoffice.EntitledFeatureTypes):
 *            the map checks it.
 *   server   a server declares the check (common.FeatureEnforcements): the
 *            Placepoint MCP at sign-in, the Agent before a data answer.
 *
 * A code in none of the three is checked by NOBODY: granting it changes
 * nothing for a user today. Such codes exist on purpose — a package can
 * already say what it includes before the check that will read it is live
 * (the Agent's tool families, the Combify sign-in code) — but an operator
 * must be able to see it at a glance, so the editor says "not checked yet"
 * instead of a word nobody outside the team knows.
 */

export type CheckedBy =
  | { kind: "console" }
  | { kind: "content" }
  | { kind: "server"; names: string; how: string | null }
  | { kind: "nobody" }

export type CheckedByInput = {
  isSurface: boolean
  featureTypeCode: number | null | undefined
  /** The content types the map checks (base maps, map layers). */
  entitledTypeCodes: ReadonlySet<number>
  checkedBy?: string | null
  checkedHow?: string | null
}

export function checkedByOf(input: CheckedByInput): CheckedBy {
  const {
    isSurface,
    featureTypeCode,
    entitledTypeCodes,
    checkedBy,
    checkedHow,
  } = input
  if (isSurface) return { kind: "console" }
  if (featureTypeCode != null && entitledTypeCodes.has(featureTypeCode)) {
    return { kind: "content" }
  }
  if (checkedBy)
    return { kind: "server", names: checkedBy, how: checkedHow ?? null }
  return { kind: "nobody" }
}

/** The row note on a code no server, no surface and no content class checks. */
export const NOT_CHECKED_NOTE = "not checked yet"

/** The "Checked by" fact on a hover card. */
export function describeCheckedBy(checkedBy: CheckedBy) {
  switch (checkedBy.kind) {
    case "console":
      return "Fusion — a capability: the lock renders where it lives in the app, and the console reads the person's access at start."
    case "content":
      return "the map — base maps and map layers are checked when the map loads for the person."
    case "server":
      return checkedBy.how
        ? `${checkedBy.names}. ${checkedBy.how}`
        : checkedBy.names
    case "nobody":
      return describeNotChecked()
  }
}

/** Why a "not checked yet" row is in the catalog at all, and what the grant does. */
export function describeNotChecked() {
  return "nothing yet — not Fusion, not the map, not a server — so granting it changes nothing for a user today. It is in the catalog so a package can already say what it includes; the check that will read it (the Agent, an MCP server) is not live yet."
}
